import { useState, useCallback, useRef } from 'react';
import { create, all } from 'mathjs';
import PlotChart, { IntersectionPoint } from './PlotChart';

const math = create(all);

interface Props {
  onClose: () => void;
  onInsertExpr: (text: string) => void;
}

/* ── Intersection finder ─────────────────────────────────────────────────── */

function numDeriv(f: (x: number) => number, x: number): number {
  const h = 1e-7;
  return (f(x + h) - f(x - h)) / (2 * h);
}

function findIntersections(
  expr1: string,
  expr2: string,
  variable: string,
  xMin: number,
  xMax: number
): IntersectionPoint[] {
  const f1 = (xv: number): number => {
    const scope: Record<string, number> = {};
    scope[variable] = xv;
    return Number(math.evaluate(expr1, scope));
  };
  const f2 = (xv: number): number => {
    const scope: Record<string, number> = {};
    scope[variable] = xv;
    return Number(math.evaluate(expr2, scope));
  };
  const diff = (xv: number) => f1(xv) - f2(xv);

  const N = 800;
  const intersections: IntersectionPoint[] = [];

  let prevVal: number | null = null;
  let prevX: number | null = null;

  for (let i = 0; i <= N; i++) {
    const xv = xMin + (i / N) * (xMax - xMin);
    let val: number;
    try { val = diff(xv); } catch { prevVal = null; prevX = null; continue; }
    if (!isFinite(val)) { prevVal = null; prevX = null; continue; }

    if (prevVal !== null && prevX !== null && prevVal * val < 0) {
      // Sign change → refine with Newton-Raphson
      let x = (prevX + xv) / 2;
      for (let iter = 0; iter < 60; iter++) {
        let fx: number;
        try { fx = diff(x); } catch { break; }
        const fpx = numDeriv(diff, x);
        if (Math.abs(fpx) < 1e-14) break;
        const xn = x - fx / fpx;
        if (Math.abs(xn - x) < 1e-10) { x = xn; break; }
        if (!isFinite(xn)) break;
        x = xn;
      }
      if (isFinite(x) && x >= xMin && x <= xMax) {
        let yVal: number;
        try { yVal = f1(x); } catch { yVal = NaN; }
        if (isFinite(yVal)) {
          const roundX = Math.round(x * 1e8) / 1e8;
          const roundY = Math.round(yVal * 1e8) / 1e8;
          if (!intersections.some(p => Math.abs(p.x - roundX) < 1e-5)) {
            intersections.push({ x: roundX, y: roundY });
          }
        }
      }
    }
    prevVal = val;
    prevX = xv;
  }

  return intersections.sort((a, b) => a.x - b.x);
}

/* ── Component ───────────────────────────────────────────────────────────── */

export default function CompareFunctionsPanel({ onClose, onInsertExpr }: Props) {
  const [expr1, setExpr1] = useState('x^2 - 2');
  const [expr2, setExpr2] = useState('2 * x - 1');
  const [xMin, setXMin] = useState(-6);
  const [xMax, setXMax] = useState(6);
  const [compared, setCompared] = useState(false);
  const [intersections, setIntersections] = useState<IntersectionPoint[]>([]);
  const [error, setError] = useState('');
  const [computing, setComputing] = useState(false);

  const input1Ref = useRef<HTMLInputElement>(null);
  const input2Ref = useRef<HTMLInputElement>(null);

  const handleCompare = useCallback(async () => {
    if (!expr1.trim() || !expr2.trim()) {
      setError('Please enter both expressions.');
      return;
    }
    setError('');
    setComputing(true);
    await new Promise(r => setTimeout(r, 60));

    try {
      // Quick validation
      const scope = { x: 1 };
      math.evaluate(expr1, scope);
      math.evaluate(expr2, scope);
    } catch (e) {
      setError(`Invalid expression: ${e instanceof Error ? e.message : String(e)}`);
      setComputing(false);
      return;
    }

    try {
      const pts = findIntersections(expr1, expr2, 'x', xMin, xMax);
      setIntersections(pts);
      setCompared(true);
    } catch (e) {
      setError(`Comparison failed: ${e instanceof Error ? e.message : String(e)}`);
    }
    setComputing(false);
  }, [expr1, expr2, xMin, xMax]);

  const series = compared
    ? [
        { expr: expr1, label: expr1, stroke: undefined },
        { expr: expr2, label: expr2, stroke: undefined },
      ]
    : [];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-violet-600 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
              <i className="ri-git-compare-line text-white text-lg"></i>
            </div>
            <div>
              <h2 className="text-white font-bold text-base">Compare Functions</h2>
              <p className="text-indigo-200 text-xs">Plot two functions and find their intersections</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/20 hover:bg-white/30 text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">

          {/* Inputs */}
          <div className="p-5 border-b border-slate-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">

              {/* Function 1 */}
              <div>
                <label className="flex items-center gap-2 text-xs font-bold text-slate-700 mb-1.5">
                  <span className="w-3 h-3 rounded-sm bg-violet-500 inline-block"></span>
                  f₁(x) — First function
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-slate-400 text-sm font-mono shrink-0">y =</span>
                  <input
                    ref={input1Ref}
                    value={expr1}
                    onChange={e => { setExpr1(e.target.value); setCompared(false); }}
                    onKeyDown={e => e.key === 'Enter' && handleCompare()}
                    placeholder="e.g. x^2 - 2"
                    className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-mono text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
                <p className="text-[10px] text-slate-400 mt-1 font-mono">Violet curve</p>
              </div>

              {/* Function 2 */}
              <div>
                <label className="flex items-center gap-2 text-xs font-bold text-slate-700 mb-1.5">
                  <span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block"></span>
                  f₂(x) — Second function
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-slate-400 text-sm font-mono shrink-0">y =</span>
                  <input
                    ref={input2Ref}
                    value={expr2}
                    onChange={e => { setExpr2(e.target.value); setCompared(false); }}
                    onKeyDown={e => e.key === 'Enter' && handleCompare()}
                    placeholder="e.g. 2*x - 1"
                    className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-mono text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  />
                </div>
                <p className="text-[10px] text-slate-400 mt-1 font-mono">Emerald curve</p>
              </div>
            </div>

            {/* X range */}
            <div className="flex items-end gap-3">
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">x min</label>
                <input
                  type="number"
                  value={xMin}
                  onChange={e => setXMin(Number(e.target.value))}
                  className="block w-20 mt-1 px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-mono text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">x max</label>
                <input
                  type="number"
                  value={xMax}
                  onChange={e => setXMax(Number(e.target.value))}
                  className="block w-20 mt-1 px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-mono text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              <button
                onClick={handleCompare}
                disabled={computing || !expr1.trim() || !expr2.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 disabled:from-slate-300 disabled:to-slate-400 text-white font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
              >
                {computing ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Computing…</>
                ) : (
                  <><i className="ri-git-compare-line"></i>Compare</>
                )}
              </button>

              {compared && (
                <button
                  onClick={() => { setCompared(false); setIntersections([]); }}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-500 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line"></i>
                  Reset
                </button>
              )}
            </div>

            {error && (
              <div className="mt-3 px-4 py-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700">
                <i className="ri-error-warning-line"></i>
                {error}
              </div>
            )}
          </div>

          {/* Plot + results */}
          {compared ? (
            <div className="p-5 space-y-5">

              {/* Dual plot */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-bold text-slate-700">Function Plot</h3>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="flex items-center gap-1.5"><span className="w-3 h-1 rounded bg-violet-400 inline-block"></span> f₁ = {expr1}</span>
                    <span className="flex items-center gap-1.5"><span className="w-3 h-1 rounded bg-emerald-400 inline-block"></span> f₂ = {expr2}</span>
                    {intersections.length > 0 && (
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-orange-500 inline-block"></span> {intersections.length} intersection{intersections.length !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                </div>
                <PlotChart
                  series={series}
                  xMin={xMin}
                  xMax={xMax}
                  intersections={intersections}
                  height={300}
                />
              </div>

              {/* Intersection results */}
              {intersections.length > 0 ? (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 bg-orange-100 rounded-lg flex items-center justify-center">
                      <i className="ri-focus-2-line text-orange-500 text-sm"></i>
                    </div>
                    <h3 className="text-sm font-bold text-slate-700">
                      {intersections.length} Intersection{intersections.length !== 1 ? 's' : ''} Found
                    </h3>
                  </div>
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="px-4 py-2.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">#</th>
                          <th className="px-4 py-2.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">x</th>
                          <th className="px-4 py-2.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">y</th>
                          <th className="px-4 py-2.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">f₁(x)</th>
                          <th className="px-4 py-2.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">f₂(x)</th>
                          <th className="px-4 py-2.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {intersections.map((pt, i) => {
                          let f1val = '—', f2val = '—';
                          try { f1val = String(Math.round(Number(math.evaluate(expr1, { x: pt.x })) * 1e8) / 1e8); } catch { /* */ }
                          try { f2val = String(Math.round(Number(math.evaluate(expr2, { x: pt.x })) * 1e8) / 1e8); } catch { /* */ }
                          return (
                            <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="px-4 py-3">
                                <span className="w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                              </td>
                              <td className="px-4 py-3 font-mono text-slate-900 font-semibold">{pt.x}</td>
                              <td className="px-4 py-3 font-mono text-slate-900 font-semibold">{pt.y}</td>
                              <td className="px-4 py-3 font-mono text-violet-600">{f1val}</td>
                              <td className="px-4 py-3 font-mono text-emerald-600">{f2val}</td>
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => onInsertExpr(`solve ${expr1} = ${expr2}`)}
                                  className="px-2.5 py-1 text-[10px] font-semibold bg-slate-100 hover:bg-orange-100 hover:text-orange-700 text-slate-600 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                                >
                                  Solve
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 bg-slate-50 rounded-xl border border-slate-200">
                  <i className="ri-focus-2-line text-3xl text-slate-300 mb-2 block"></i>
                  <p className="text-sm text-slate-500 font-semibold">No intersections found</p>
                  <p className="text-xs text-slate-400 mt-1">The functions don&apos;t cross in x = [{xMin}, {xMax}]</p>
                  <p className="text-xs text-slate-400 mt-0.5">Try expanding the x range</p>
                </div>
              )}

              {/* Extra info */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Solve for intersections', example: `solve ${expr1} = ${expr2}`, icon: 'ri-equals-line', color: 'violet' },
                  { label: 'Derivative of f₁', example: `derivative of ${expr1}`, icon: 'ri-arrow-right-up-line', color: 'emerald' },
                ].map(item => (
                  <button
                    key={item.label}
                    onClick={() => { onInsertExpr(item.example); onClose(); }}
                    className={`text-left p-3 rounded-xl border-2 border-${item.color}-100 bg-${item.color}-50 hover:border-${item.color}-300 transition-all cursor-pointer group`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <i className={`${item.icon} text-${item.color}-500 text-sm`}></i>
                      <span className={`text-xs font-bold text-${item.color}-700`}>{item.label}</span>
                    </div>
                    <code className="text-[10px] text-slate-500 font-mono">{item.example}</code>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* Empty state */
            <div className="flex-1 flex flex-col items-center justify-center py-16 text-center px-6">
              <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mb-4">
                <i className="ri-git-compare-line text-indigo-500 text-3xl"></i>
              </div>
              <h3 className="text-base font-bold text-slate-700 mb-2">Ready to Compare</h3>
              <p className="text-slate-400 text-sm max-w-xs mb-6">
                Enter two function expressions and click &ldquo;Compare&rdquo; to see them plotted together with intersection points marked.
              </p>
              <div className="grid grid-cols-2 gap-3 text-left w-full max-w-sm">
                {[
                  { f1: 'x^2 - 2', f2: '2*x - 1', note: 'Parabola vs line' },
                  { f1: 'sin(x)', f2: 'cos(x)', note: 'Trig functions' },
                  { f1: 'x^3 - x', f2: 'x', note: 'Cubic vs line' },
                  { f1: 'e^x', f2: '2*x + 1', note: 'Exponential vs linear' },
                ].map((eg, i) => (
                  <button
                    key={i}
                    onClick={() => { setExpr1(eg.f1); setExpr2(eg.f2); }}
                    className="p-3 text-left bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-300 rounded-xl transition-all cursor-pointer group"
                  >
                    <p className="text-[10px] font-bold text-indigo-600 mb-1">{eg.note}</p>
                    <code className="text-[9px] text-slate-500 font-mono block">f₁ = {eg.f1}</code>
                    <code className="text-[9px] text-slate-500 font-mono block">f₂ = {eg.f2}</code>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
